import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LogIn, LogOut, ShieldCheck, ShoppingBag, User as UserIcon } from "lucide-react";
import type { User } from "@supabase/supabase-js";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth, signOutAndGoHome } from "@/hooks/use-auth";

/** Foto de Google (si existe) o inicial del nombre/correo. */
export function UserAvatar({ user, className = "h-7 w-7 text-[11px]" }: { user: User; className?: string }) {
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>;
  const photo = (meta.avatar_url ?? meta.picture) as string | undefined;
  const name = (meta.full_name ?? meta.name ?? user.email ?? "U") as string;
  if (photo) {
    return (
      <img
        src={photo}
        alt=""
        referrerPolicy="no-referrer"
        className={`${className} shrink-0 rounded-full object-cover`}
      />
    );
  }
  return (
    <span className={`${className} grid shrink-0 place-items-center rounded-full bg-primary font-bold text-primary-foreground`}>
      {name.trim().slice(0, 1).toUpperCase()}
    </span>
  );
}

/** Botón de "Cerrar sesión" reutilizable (Mi cuenta, panel admin, menús). */
export function SignOutButton({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={() => void signOutAndGoHome()}
      className={`inline-flex items-center gap-2 rounded-full border border-destructive/40 px-4 py-2 text-sm font-semibold text-destructive transition hover:bg-destructive/10 ${className}`}
    >
      <LogOut className="h-4 w-4" /> {t("nav.logout", "Cerrar sesión")}
    </button>
  );
}

/**
 * Ícono de usuario del encabezado. Sin sesión: enlace a iniciar sesión.
 * Con sesión: avatar que abre un menú con "Cerrar sesión" visible.
 */
export function AccountMenu({
  triggerClassName = "",
  signedOutIcon,
  label,
}: {
  triggerClassName?: string;
  signedOutIcon?: React.ReactNode;
  label?: React.ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const { user, isAdmin } = useAuth();

  if (!user) {
    return (
      <Link to="/auth" aria-label={i18n.language.startsWith("en") ? "Sign in" : "Iniciar sesión"} className={triggerClassName}>
        {signedOutIcon ?? <UserIcon className="h-4 w-4" />}
        {label}
      </Link>
    );
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger aria-label={t("nav.myAccount", "Mi cuenta")} className={triggerClassName}>
        <UserAvatar user={user} />
        {label}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="z-[80] w-64">
        <DropdownMenuLabel className="truncate text-xs font-normal text-muted-foreground">
          {user.email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/mi-cuenta" className="flex items-center gap-2 py-2.5">
            <UserIcon className="h-4 w-4" /> {t("nav.myAccount", "Mi cuenta")}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/mi-cuenta" className="flex items-center gap-2 py-2.5">
            <ShoppingBag className="h-4 w-4" /> {t("nav.myBookings", "Mis reservas")}
          </Link>
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem asChild>
            <Link to="/admin/dashboard" className="flex items-center gap-2 py-2.5">
              <ShieldCheck className="h-4 w-4" /> {t("nav.admin", "Panel administrativo")}
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => void signOutAndGoHome()}
          className="flex items-center gap-2 py-3 font-semibold text-destructive focus:bg-destructive/10 focus:text-destructive"
        >
          <LogOut className="h-4 w-4" /> {t("nav.logout", "Cerrar sesión")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}


